import { Suspense, lazy, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useSearchParams,
  useNavigate,
  Outlet,
  useParams,
} from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import RefinerUtils from '@/refiner/RefinerUtils';
import { useGetUserHashLoginQuery, useGetUserQuery } from '@/api/UserQuery';
import ViewportProvider from '@/common/context/ViewPort/ViewPortProvider';
import Spinner from '@/components/Spinner/Spinner';
import './App.scss';

import 'react-toastify/dist/ReactToastify.css';
import { setMemberApprovalInfo, setUserDetails } from '@/store/reducers/user';
import { useAppDispatch } from '@/store/hooks';
import { useLazyGetAccountClientsQuery } from '@/api/AccountsQuery';
import { setAccountMembers } from '@/store/reducers/account';
import { getAccountMembersPillInfo } from '@/utilities/AccountUtils';
import type { IAccountMember } from '@/common/interfaces/user.interface';
import {
  AUTH_REQUIRED_COPY,
  ROUTES_WITHOUT_HEADER,
  isStoreCheckoutRoute,
} from '@/constants/Common';
import Result from '@/components/Result/Result';
import { buildAuthRedirectUrl, shouldPerformAuthRedirect } from '@/utilities/authRedirect.utils';
import { useGetCartItemsQuery } from '@/api/CartQuery';
import { useGetPrintConfigQuery } from '@/api/PrintConfigQuery';
import { setCartCountObject } from '@/store/reducers/cart';
import { setStoreCountObject } from '@/store/reducers/storeCart';
import MixpanelUtils from '@/mixpanel/MixpanelUtils';
import { showToastrMessage } from '@/utilities/Toastr.Utils';
import { decrypt } from '@/utilities/CryptoUtils';
import { UTMProvider } from '@/common/context/UTMContext';
import { BraintreeProvider } from '@/components/BraintreeDropIn/BraintreeProvider';
import { useLazyGetBraintreeClientTokenQuery } from '@/api/BraintreePaypalQuery';
import {
  fetchBraintreeFailure,
  fetchBraintreeRequest,
  fetchBraintreeSuccess,
} from '@/store/reducers/braintree';
import { captureHQVisitedEvent } from '@/mixpanel/MixpanelEvents';
import { persistInboundEntryFromSearchParams } from '@/mixpanel/inboundEntryTracking';
import { usePublicRouteInfo } from '@/hooks/usePublicRouteInfo';
import { isStoreCartPageViewport } from '@/modules/Cart/StoreCart/helpers/storeCartNavigation.utils';
import { clIdentifyAndUpdateUser, getClIdentifyDedupeTraits } from '@/CustomerLabs/ClUtils';
import StoreCartPage from '@/modules/Cart/StoreCart/StoreCartPage';
import StoreCheckout from '@/modules/Orders/CreateOrders/StoreCheckout/StoreCheckout';
import { StoreCheckoutUiVariationProvider } from '@/modules/Orders/CreateOrders/StoreCheckout/context/StoreCheckoutUiVariationContext';
import { STORE_CHECKOUT_LOADING_TIP } from '@/modules/Orders/CreateOrders/StoreCheckout/constants/storeCheckout.constants';
import { HqPageViewProvider } from '@/modules/Home/context/HqPageViewContext';
import { ORDER_DETAIL_PAGE_VIEW_VALUES } from '@/modules/Orders/OrderDetail/constants/orderDetailPageView.constants';
import { PARTICIPANT_PORTAL_COPY } from '@/modules/ParticipantPortal/constants/participantPortal.constants';
import { ParticipantSessionProvider } from '@/modules/ParticipantPortal/context/ParticipantSessionContext';
import { useParticipantAuthGate } from '@/modules/ParticipantPortal/hooks/useParticipantAuthGate';

/** Lazy loaded components. */
const Portal = lazy(() => import('@/modules/Home/Portal/Portal'));
const PortalHeader = lazy(() => import('@/modules/Home/Header/PortalHeader'));
const Invoices = lazy(() => import('@/modules/Invoices/Invoices'));
const FavoritedAll = lazy(() => import('@/modules/Favorites/FavoritedAll/FavoritedAll'));
const DesignPage = lazy(() => import('@/modules/Designs/DesignPage/DesignPage'));
const OrdersSeeAll = lazy(() => import('@/modules/Orders/OrdersSeeAll/OrdersSeeAll'));
const OrderPage = lazy(() => import('@/modules/Orders/OrderPage/OrderPage'));
const DesignsSeeAll = lazy(() => import('@/modules/Designs/DesignsSeeAll/DesignsSeeAll'));
const InvoicesSeeAll = lazy(() => import('@/modules/Invoices/InvoicesSeeAll/InvoicesSeeAll'));
const InvoicePage = lazy(() => import('@/modules/Invoices/InvoicePage/InvoicePage'));
const PageNotFound = lazy(() => import('@/components/PageNotFound/PageNotFound'));
const GroupOrder = lazy(() => import('@/modules/Orders/CreateOrders/GroupOrder/GroupOrder'));
const CheckoutV4 = lazy(
  () => import('@/modules/Orders/CreateOrders/RegularOrder/CheckoutV4/CheckoutV4')
);
const EditStoreCheckout = lazy(
  () => import('@/modules/Orders/CreateOrders/StoreCheckout/EditStoreCheckout/EditStoreCheckout')
);
const EditParticipantOrderDetailsWrapper = lazy(
  () =>
    import(
      '@/modules/Orders/OrderDetail/EditParticipantOrderDetails/EditParticipantOrderDetailsWrapper'
    )
);
const PermanentStoreParticipantsWrapper = lazy(
  () =>
    import(
      '@/modules/Orders/OrderDetail/PermanentStoreParticipants/PermanentStoreParticipantsWrapper'
    )
);
const ParticipantPortal = lazy(() => import('@/modules/ParticipantPortal/ParticipantPortal'));
const ParticipantOrdersSeeAll = lazy(
  () => import('@/modules/ParticipantPortal/ParticipantOrdersSeeAll/ParticipantOrdersSeeAll')
);
const ParticipantOrderPage = lazy(
  () => import('@/modules/ParticipantPortal/ParticipantOrderPage/ParticipantOrderPage')
);
const ParticipantPortalHeader = lazy(
  () => import('@/modules/ParticipantPortal/ParticipantPortalHeader/ParticipantPortalHeader')
);

/**
 * Storefront thank-you links here with a participant id.
 * Client HQ already renders ParticipantOrderPage on this path.
 * A participant session uses `/order/:participantId` for that same page.
 */
const ParticipantPurchaseRedirect = () => {
  const { orderId } = useParams();
  return <Navigate to={orderId ? `/order/${orderId}` : '/'} replace />;
};

const App = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [skip, setSkip] = useState<boolean>(true);
  const [hashLoginCheck, setHashLoginCheck] = useState<boolean>(false);
  const currentUrl = useLocation();
  const navigate = useNavigate();
  const { isPublicRoute, isSpecificPublicRoute } = usePublicRouteInfo();
  const [windowWidth, setWindowWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [getBraintreeClientToken] = useLazyGetBraintreeClientTokenQuery();

  /** Skip this call if there is no hash in the route */
  const {
    error: hashLoginErr,
    isLoading: isHashLoginLoading,
    data: hashUserLoginData,
  } = useGetUserHashLoginQuery(searchParams.get('login'), {
    skip: skip || !searchParams.get('login'),
  });

  useEffect(() => {
    if (!hashLoginCheck) {
      setSkip(!searchParams.get('login'));
    }
    setHashLoginCheck(true);
  }, [hashLoginCheck, searchParams]);

  useLayoutEffect(() => {
    persistInboundEntryFromSearchParams(new URLSearchParams(searchParams.toString()));
  }, [searchParams]);

  /* If login data is available, remove 'login' from search params */
  useEffect(() => {
    if (hashUserLoginData) {
      searchParams.delete('login');
      setSearchParams(searchParams);
    }
  }, [hashUserLoginData, searchParams, setSearchParams, dispatch]);

  useEffect(() => {
    const queryParams = new URLSearchParams(currentUrl.search);
    const iv = queryParams.get('iv');
    const encryptedData = queryParams.get('data');
    const key = queryParams.get('key');

    if (iv && encryptedData && key) {
      try {
        const decryptedParams = decrypt(
          decodeURIComponent(encryptedData),
          decodeURIComponent(key),
          decodeURIComponent(iv)
        );
        const params = JSON.parse(decryptedParams);

        if (params.status === 200) {
          showToastrMessage(params.message, 'success');
        } else {
          showToastrMessage(params.message, 'failure');
        }

        queryParams.delete('iv');
        queryParams.delete('data');
        queryParams.delete('key');
        navigate({ search: queryParams.toString() }, { replace: true });
      } catch (error) {
        console.error('Failed to decrypt parameters:', error);
      }
    }
  }, [currentUrl, navigate]);

  /** This has to be skipped till the hash login check is done if there is a hash in the route */
  const [isParticipantMode, setIsParticipantMode] = useState(false);

  const { error, isLoading, data } = useGetUserQuery('isLoggedIn', {
    skip:
      (searchParams.get('login') && !hashUserLoginData) ||
      isPublicRoute ||
      isParticipantMode ||
      false,
  });

  /**
   * A participant has no client account, so their session is only looked up once
   * the client session check has failed.
   */
  const clientAuthFailed = Boolean(hashLoginErr || error) && !isPublicRoute;
  const {
    isParticipant,
    isChecking: isCheckingParticipant,
    email: participantEmail,
    profile: participantProfile,
  } = useParticipantAuthGate(clientAuthFailed);

  /** Stops the client session query from retrying behind the participant portal. */
  useEffect(() => {
    if (isParticipant && !isParticipantMode) {
      setIsParticipantMode(true);
    }
  }, [isParticipant, isParticipantMode]);

  const [accountMembersQuery] = useLazyGetAccountClientsQuery();

  const customerLabsIdentifyDedupe = useMemo(
    () => (data ? getClIdentifyDedupeTraits(data) : null),
    [data]
  );

  useEffect(() => {
    if (!customerLabsIdentifyDedupe) {
      return () => {};
    }
    return clIdentifyAndUpdateUser(customerLabsIdentifyDedupe);
  }, [customerLabsIdentifyDedupe]);

  useEffect(() => {
    if (data) {
      MixpanelUtils.identify(data.email);
      MixpanelUtils.setPeople({ $email: data.email });
      RefinerUtils.identify(data.email);
      if (!isStoreCheckoutRoute(currentUrl.pathname)) {
        RefinerUtils.track('HQ Visited');
        captureHQVisitedEvent(data.email);
      }
    }

    async function fetchAccountMemberData() {
      try {
        if (data?.account_id) {
          const { data: accountMembers } = await accountMembersQuery(data.account_id);
          const members = accountMembers ?? [];

          /** This will be used to set the color pill object for each member of an account. */
          if (Array.isArray(members) && members.length) {
            dispatch(setAccountMembers(getAccountMembersPillInfo(members as IAccountMember[])));
          }
        }
      } catch (err) {
        /* empty */
      }
    }

    fetchAccountMemberData();
  }, [currentUrl.pathname, data, accountMembersQuery, dispatch]);

  useEffect(() => {
    if (data) {
      dispatch(setUserDetails(data));
      dispatch(
        setMemberApprovalInfo({
          isMemberModalSeen: data.is_member_modal_seen,
          memberApprovalStatus: data.member_approval_status,
        })
      );
    }
  }, [data, dispatch]);

  const fetchClientToken = useCallback(
    async (userId?: number) => {
      dispatch(fetchBraintreeRequest());
      try {
        const { data: braintreeClientTokenRs } = await getBraintreeClientToken(userId);
        dispatch(fetchBraintreeSuccess(braintreeClientTokenRs?.clientToken || ''));
      } catch (err) {
        dispatch(fetchBraintreeFailure('Failed to fetch braintree client token.'));
        showToastrMessage('Failed to fetch braintree client token.', 'failure');
      }
    },
    [getBraintreeClientToken, dispatch]
  );

  /**
   * This hook helps to get the client token from the server and set the Braintree DropIn UI.
   */
  useEffect(() => {
    if (data?.id && !isPublicRoute) {
      fetchClientToken(data.id);
    }
  }, [data, isPublicRoute, fetchClientToken]);

  useEffect(() => {
    if (isPublicRoute && isSpecificPublicRoute('INVOICE_PAYMENT')) {
      fetchClientToken();
    }
  }, [fetchClientToken, isPublicRoute, isSpecificPublicRoute]);

  /** CMS print catalog: pattern / colorblock method and treatment uids. Public, no auth. */
  useGetPrintConfigQuery();

  /** Gets cart count data — skipped for participants (no client cart session). */
  const { data: cartCountData } = useGetCartItemsQuery(
    {
      countAndTotalOnly: true,
    },
    { skip: isParticipantMode || isParticipant || isCheckingParticipant }
  );

  const { data: storeCountData } = useGetCartItemsQuery(
    {
      countAndTotalOnly: true,
      isStore: true,
    },
    { skip: isParticipantMode || isParticipant || isCheckingParticipant }
  );

  /** Sets cart count object */
  useEffect(() => {
    if (cartCountData) {
      dispatch(setCartCountObject(cartCountData));
    }
  }, [cartCountData, dispatch]);

  useEffect(() => {
    if (storeCountData) {
      dispatch(setStoreCountObject(storeCountData));
    }
  }, [storeCountData, dispatch]);

  const isOnStoreCheckoutRoute = isStoreCheckoutRoute(currentUrl.pathname);
  const appLoadingTip = isOnStoreCheckoutRoute ? STORE_CHECKOUT_LOADING_TIP : 'Almost there...';

  /** This will be replaced with respective loading screen once we have one  */
  if ((isLoading || isHashLoginLoading) && !isParticipant) {
    return <Spinner tip={appLoadingTip} size="default" />;
  }

  if (isCheckingParticipant) {
    return <Spinner tip={PARTICIPANT_PORTAL_COPY.LOADING_SESSION} size="default" />;
  }

  /**
   * Participants get the same routes as clients, rendered in participant view
   * against the participant session.
   */
  if (isParticipant) {
    return (
      <Suspense fallback={<Spinner tip={PARTICIPANT_PORTAL_COPY.LOADING_PORTAL} size="default" />}>
        <ViewportProvider>
          <HqPageViewProvider initialPageView={ORDER_DETAIL_PAGE_VIEW_VALUES.PARTICIPANT}>
            <ParticipantSessionProvider email={participantEmail} profile={participantProfile}>
              <div className="customer-portal">
                <ParticipantPortalHeader />
                <Routes>
                  <Route path="/" element={<ParticipantPortal />} />
                  <Route path="/orders/list" element={<ParticipantOrdersSeeAll />} />
                  <Route path="/order/:orderId" element={<ParticipantOrderPage />} />
                  <Route
                    path="/participant-order/:orderId"
                    element={<ParticipantPurchaseRedirect />}
                  />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                <ToastContainer />
              </div>
            </ParticipantSessionProvider>
          </HqPageViewProvider>
        </ViewportProvider>
      </Suspense>
    );
  }

  /**
   * If there an error (unauthenticated), then redirect to login page.
   */
  if (clientAuthFailed) {
    const redirectURL = buildAuthRedirectUrl(window.location.search);

    if (shouldPerformAuthRedirect(redirectURL)) {
      window.location.replace(redirectURL);
      return null;
    }

    return (
      <div className="auth-required">
        <Result
          primaryText={AUTH_REQUIRED_COPY.PRIMARY}
          secondarytText={AUTH_REQUIRED_COPY.SECONDARY}
        />
      </div>
    );
  }

  const shouldHideHeader =
    ROUTES_WITHOUT_HEADER.includes(currentUrl.pathname) ||
    /^\/store\/edit\/[^/]+$/.test(currentUrl.pathname) ||
    /^\/order\/[^/]+\/edit-participants$/.test(currentUrl.pathname) ||
    /^\/order\/[^/]+\/permanent-store-participants$/.test(currentUrl.pathname) ||
    (currentUrl.pathname === '/store-cart' && isStoreCartPageViewport(windowWidth));

  return (
    <Suspense fallback={<Spinner tip={appLoadingTip} size="default" />}>
      <ViewportProvider>
        <HqPageViewProvider>
          <div className="customer-portal">
            {!shouldHideHeader && <PortalHeader />}
            <UTMProvider>
              <Routes>
                <Route path="/" element={<Portal />} />
                <Route path="/payInvoice" element={<Invoices />} />
                <Route path="/favorites/list" element={<FavoritedAll />} />
                <Route path="/design/:designId" element={<DesignPage />} />
                <Route
                  path="/order/:orderId/edit-participants"
                  element={<EditParticipantOrderDetailsWrapper />}
                />
                <Route
                  path="/order/:orderId/permanent-store-participants"
                  element={<PermanentStoreParticipantsWrapper />}
                />
                <Route path="/order/:orderId" element={<OrderPage />} />
                <Route path="/participant-order/:orderId" element={<ParticipantOrderPage />} />
                <Route
                  path="/favorited/designs"
                  element={<FavoritedAll limit={10} fetchDesign />}
                />
                <Route path="/favorited/products" element={<FavoritedAll limit={10} />} />
                <Route path="/orders/list" element={<OrdersSeeAll />} />
                <Route path="/designs/list" element={<DesignsSeeAll />} />
                <Route path="/invoices/list" element={<InvoicesSeeAll />} />
                <Route
                  element={
                    <BraintreeProvider>
                      <Outlet />
                    </BraintreeProvider>
                  }
                >
                  <Route path="/invoice/:invoiceId" element={<InvoicePage />} />
                  <Route path="/pay/invoice/:invoiceId" element={<InvoicePage />} />
                </Route>
                <Route path="/store/new" element={<GroupOrder />} />
                <Route path="/store-cart" element={<StoreCartPage />} />
                <Route path="/store/edit/:orderId" element={<EditStoreCheckout />} />
                <Route
                  element={
                    <BraintreeProvider>
                      <Outlet />
                    </BraintreeProvider>
                  }
                >
                  <Route path="/regular-order/new" element={<CheckoutV4 />} />
                  <Route
                    path="/store-checkout/new"
                    element={
                      <StoreCheckoutUiVariationProvider>
                        <StoreCheckout />
                      </StoreCheckoutUiVariationProvider>
                    }
                  />
                </Route>
                {/* <Route path="/" element={<Navigate to="/portal" replace />} /> */}
                <Route path="*" element={<PageNotFound />} />
              </Routes>
            </UTMProvider>
            <ToastContainer />
          </div>
        </HqPageViewProvider>
      </ViewportProvider>
    </Suspense>
  );
};

export default App;
